import { useEffect, useState } from 'react';
import type React from 'react';
import { ArrowSquareOut, ArrowsOut, CircleNotch, DownloadSimple, Eye, FileText } from '@phosphor-icons/react';
import { ladeAnhangBlob } from '../../../lib/emails/anhangDatei';

interface AnhangVorschauProps {
  anhangId: string;
  dateiname: string;
  dateityp: string | null;
  hatGraphRef: boolean;
  /** Mobil: 44-px-Ziele. */
  mobil?: boolean;
}

export default function AnhangVorschau({ anhangId, dateiname, dateityp, hatGraphRef, mobil = false }: AnhangVorschauProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gross, setGross] = useState(false);
  // Lazy: Vorschau erst auf Klick laden — sonst feuern bei Mails mit vielen Anhaengen
  // N gleichzeitige Edge-Function-Aufrufe (Graph + Auth) und ueberlasten Supabase.
  const [zeigen, setZeigen] = useState(false);

  // Anhänge stammen aus eingehenden E-Mails und sind angreifer-kontrolliert.
  // Daher NUR anhand des echten MIME-Typs klassifizieren (kein Filename-Raten)
  // und ausschließlich harmlose Vorschau-Typen zulassen — insbesondere KEIN
  // image/svg+xml oder text/html: als same-origin Blob würden die Skripte im
  // Origin ausführen (XSS → Session-Token abgreifbar).
  const typ = (dateityp || '').split(';')[0].trim().toLowerCase();
  const istBild = /^image\/(png|jpe?g|gif|webp|bmp|tiff?)$/.test(typ);
  const istPdf = typ === 'application/pdf';
  const vorschaubar = istBild || istPdf;
  // Erzwungener, sanitisierter MIME-Typ für den Blob — nie den DB-Wert direkt vertrauen.
  const sicherMime = istPdf ? 'application/pdf' : istBild ? typ : null;

  useEffect(() => {
    if (!zeigen || !hatGraphRef || !vorschaubar) return;
    let aktiv = true;
    let objUrl: string | null = null;
    setLaedt(true); setFehler(null);
    ladeAnhangBlob(anhangId, sicherMime)
      .then((blob) => { if (!aktiv) return; objUrl = URL.createObjectURL(blob); setUrl(objUrl); })
      .catch((e) => { if (aktiv) setFehler((e as Error).message); })
      .finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [zeigen, anhangId, dateityp, hatGraphRef, vorschaubar]);

  async function herunterladen() {
    try {
      const blob = url ? await (await fetch(url)).blob() : await ladeAnhangBlob(anhangId, dateityp);
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl; a.download = dateiname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(dl), 2000);
    } catch { /* Fehler wird oben schon angezeigt */ }
  }

  const knopf: React.CSSProperties = {
    height: mobil ? 44 : 30, padding: '0 10px', borderRadius: 10, border: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 12, fontWeight: 700, background: 'var(--bg)', color: 'var(--ink)', cursor: 'pointer', textDecoration: 'none',
  };

  if (!hatGraphRef) {
    return <p style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 600, color: 'var(--warn)' }}>Keine Postfach-Referenz — Vorschau/Download nicht verfügbar.</p>;
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {vorschaubar && laedt && (
        <div className="flex items-center justify-center" style={{ height: 160, borderRadius: 16, background: 'var(--fill2)' }}>
          <CircleNotch size={18} className="animate-spin" color="var(--ink3)" />
        </div>
      )}
      {vorschaubar && fehler && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--bad)' }}>Vorschau fehlgeschlagen: {fehler}</p>
      )}
      {vorschaubar && url && !laedt && (
        istBild ? (
          <img
            src={url}
            alt={dateiname}
            onClick={() => setGross(true)}
            className="cursor-zoom-in"
            style={{ maxHeight: 288, maxWidth: '100%', width: 'auto', borderRadius: 16, boxShadow: 'inset 0 0 0 1px var(--line)', alignSelf: 'flex-start' }}
          />
        ) : (
          // Kein sandbox: Chromes PDF-Viewer rendert im vollstaendig sandboxed Iframe nicht.
          // Sicher trotzdem: der Blob-Typ ist per Allowlist hart auf application/pdf gelockt
          // (Bytes werden als PDF behandelt, nie als HTML/SVG) -> kein Skript-Kontext, keine XSS.
          <iframe src={url} title={dateiname} referrerPolicy="no-referrer"
            style={{ width: '100%', height: gross ? '70vh' : 360, border: 'none', borderRadius: 16, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }} />
        )
      )}

      <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
        {vorschaubar && !zeigen && (
          <button type="button" onClick={() => setZeigen(true)} style={knopf}>
            <Eye size={13} weight="bold" /> Vorschau anzeigen
          </button>
        )}
        <button type="button" onClick={herunterladen} style={knopf}>
          <DownloadSimple size={13} weight="bold" /> Herunterladen
        </button>
        {/* Top-Level-Navigation zu einem Blob nur fuer PDF (kann keinen Script ausfuehren); NICHT fuer Bilder/andere. */}
        {istPdf && url && (
          <a href={url} target="_blank" rel="noreferrer" style={knopf}>
            <ArrowSquareOut size={13} weight="bold" /> Im Tab öffnen
          </a>
        )}
        {istPdf && url && (
          <button type="button" onClick={() => setGross((g) => !g)} style={knopf}>
            <ArrowsOut size={13} weight="bold" /> {gross ? 'Kleiner' : 'Größer'}
          </button>
        )}
        {!vorschaubar && (
          <span className="flex items-center" style={{ gap: 5, fontSize: 12, color: 'var(--ink3)' }}><FileText size={13} /> Keine Inline-Vorschau</span>
        )}
      </div>
    </div>
  );
}
